import { fetchToApi } from "@data/lib/fetch-to-api";
import { AuthenticationResponseType, ResponseType } from "@data/types";

/**
 * Attempts to refresh the authentication token using the provided refresh token.
 *
 * @param refreshToken - The refresh token to be used for obtaining a new authentication token.
 * @param onError - Callback function to be executed if the token refresh fails.
 * @param onSuccess - Callback function to be executed if the token refresh is successful.
 * Receives the new token and refresh token as arguments.
 * @returns An object containing error information if the token refresh fails.
 */
export const tryToRefreshToken = async (
  refreshToken: string,
  onError: () => void,
  onSuccess: (token: string, refreshToken: string) => void
) => {
  try {
    const response = await fetchToApi("/auth-refresh", "POST", {
      refreshToken: refreshToken
    });

    if (!response.ok) {
      onError();
      return {
        error: "forbidden",
        data: null,
        code: 403,
        message: "forbidden"
      };
    }

    const data = await (async () => {
      try {
        return (await response.json()) as ResponseType<AuthenticationResponseType | null>;
      } catch (e) {
        return null;
      }
    })();

    if (data?.data?.token && data?.data?.refreshToken) {
      const { token, refreshToken } = data.data;
      onSuccess(token, refreshToken);
      return;
    }

    onError();

    return {
      error: "forbidden",
      data: null,
      code: 403,
      message: "forbidden"
    };
  } catch (e) {
    onError();
  }
};
